import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { useLicense } from '../context/LicenseContext';
import { getQuickActions } from '../config/navigation';
import { HelpCenterPanel } from './HelpCenterLink';
import { getPageContext } from '../config/fieldGlossary';
import {
  Zap,
  Users,
  Shield,
  Settings,
  ArrowLeftRight,
  Wallet,
  UserPlus,
  FileEdit,
  FileSignature,
  ChevronUp,
  ChevronDown,
  X,
  HelpCircle,
  Sparkles,
} from 'lucide-react';

// Icon mapping
const ICONS = {
  Users,
  Shield,
  Settings,
  ArrowLeftRight,
  Wallet,
  UserPlus,
  FileEdit,
  FileSignature,
  HelpCircle,
  Sparkles,
};

/**
 * QuickActions - Floating action button with contextual shortcuts
 *
 * Shows role-appropriate quick actions in a expandable menu.
 * Positioned in bottom-right corner, can be minimized.
 */
const QuickActions = () => {
  const { user } = useContext(UserContext);
  const { isModuleEnabled } = useLicense();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(() => {
    try {
      return localStorage.getItem('quickActionsMinimized') === 'true';
    } catch {
      return false;
    }
  });
  const menuRef = useRef(null);

  const userRoles = (user?.role_slugs || []).map(s => String(s).toLowerCase());

  // Get available quick actions for this user
  const quickActions = getQuickActions({
    userRoles,
    isModuleEnabled,
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Persist minimized state
  useEffect(() => {
    try {
      localStorage.setItem('quickActionsMinimized', String(isMinimized));
    } catch {
      // Ignore storage errors
    }
  }, [isMinimized]);

  // Don't render if no user or no actions available
  if (!user || quickActions.length === 0) return null;

  const handleAction = (action) => {
    setIsOpen(false);

    // Special handling for Help & AI Assistant - open the slide-out panel
    if (action.key === 'help_assistant') {
      setHelpPanelOpen(true);
      return;
    }

    if (action.path.startsWith('/admin#')) {
      // Admin panel links open in new tab with security attributes
      window.open(action.path.replace('#', ''), '_blank', 'noopener,noreferrer');
    } else {
      navigate(action.path);
    }
  };

  // Get page context for help panel
  const pageContext = getPageContext(location.pathname);

  const getIcon = (iconName) => {
    const IconComponent = ICONS[iconName];
    return IconComponent ? <IconComponent className="w-4 h-4" /> : <Zap className="w-4 h-4" />;
  };

  // Minimized state - just show a small toggle button
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        <button
          onClick={() => setIsMinimized(false)}
          data-tour="quick-actions"
          className="p-2 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="Show quick actions"
          title="Show quick actions"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-40" ref={menuRef}>
      {/* Action menu */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 mb-2 w-[calc(100vw-2rem)] sm:w-64 max-w-xs bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in slide-in-from-bottom-2">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Quick Actions
            </span>
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"
              aria-label="Minimize quick actions"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
          <div className="py-2 max-h-80 overflow-y-auto">
            {quickActions.map((action) => (
              <button
                key={action.key}
                onClick={() => handleAction(action)}
                className="w-full px-4 py-3 sm:py-2.5 flex items-center gap-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left min-h-[44px]"
              >
                <span className="flex-shrink-0 p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                  {getIcon(action.icon)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {action.label}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {action.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Press <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-xs">⌘K</kbd> for search
            </span>
          </div>
        </div>
      )}

      {/* FAB button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-tour="quick-actions"
        className={`p-4 rounded-full shadow-lg transition-all duration-200 ${
          isOpen
            ? 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
        aria-label={isOpen ? 'Close quick actions' : 'Open quick actions'}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <Zap className="w-5 h-5" />
        )}
      </button>

      {/* Help Center Panel */}
      {helpPanelOpen && (
        <HelpCenterPanel
          onClose={() => setHelpPanelOpen(false)}
          pageContext={pageContext}
          currentPath={location.pathname}
        />
      )}
    </div>
  );
};

export default QuickActions;
